import React, { useRef, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import { PageName } from "../About/About.styles";
import { contentVariants, useIsFirstLoad } from "../../variants";
import { PROJECTS } from "./data";
import { Wrapper, PageContainer, HeaderSection, BackButton, StripDivider } from "./styles";
import SpotlightView from "./SpotlightView";
import CarouselStrip from "./CarouselStrip";
import useCarouselNavigation from "./useCarouselNavigation";

const SWIPE_THRESHOLD = 50;
const WHEEL_COOLDOWN = 600;

const Projects = () => {
  const history = useHistory();
  const isFirstLoad = useIsFirstLoad();
  const { selectedIndex, setSelectedIndex, goNext, goPrev } =
    useCarouselNavigation(PROJECTS.length);
  const selectedProject = PROJECTS[selectedIndex];

  const touchStart = useRef(null);
  const spotlightRef = useRef(null);
  const wheelCooldown = useRef(false);
  const onTouchStart = useCallback((e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);

  const onTouchEnd = useCallback(
    (e) => {
      if (touchStart.current === null) return;
      const dx = touchStart.current.x - e.changedTouches[0].clientX;
      const dy = touchStart.current.y - e.changedTouches[0].clientY;

      if (Math.abs(dx) > Math.abs(dy)) {
        if (Math.abs(dx) > SWIPE_THRESHOLD) {
          if (dx > 0) goNext();
          else goPrev();
        }
      } else {
        if (Math.abs(dy) > SWIPE_THRESHOLD) {
          if (dy > 0) goNext();
          else goPrev();
        }
      }
      touchStart.current = null;
    },
    [goNext, goPrev]
  );

  useEffect(() => {
    const el = spotlightRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      e.preventDefault();
      if (wheelCooldown.current) return;
      if (Math.abs(e.deltaY) < 5) return;

      wheelCooldown.current = true;
      if (e.deltaY > 0) goNext();
      else goPrev();

      setTimeout(() => {
        wheelCooldown.current = false;
      }, WHEEL_COOLDOWN);
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [goNext, goPrev]);

  return (
    <Wrapper>
      <motion.div
        variants={contentVariants}
        initial={isFirstLoad ? false : "hidden"}
        animate="visible"
        exit="exit"
      >
        <PageContainer>
          <HeaderSection>
            <BackButton onClick={() => history.goBack()}>&#8592;</BackButton>
            <PageName
              colors={{ right: ["magenta", "cyan"] }}
              pos="right"
              style={{ position: "relative", top: 0, right: 0, fontSize: "clamp(22px, 4vmax, 48px)" }}
            >
              ./projects
            </PageName>
          </HeaderSection>

          <div
            ref={spotlightRef}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            style={{ flex: 1, minHeight: 0, display: "flex", width: "100%" }}
          >
            <SpotlightView project={selectedProject} />
          </div>

          <StripDivider
            from={selectedProject.gradient.from}
            to={selectedProject.gradient.to}
          />

          <CarouselStrip
            projects={PROJECTS}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
          />
        </PageContainer>
      </motion.div>
    </Wrapper>
  );
};

export default Projects;
